import React, { FC, memo, useState } from 'react';
import { useOrderHistory } from '~/features/club';
import { OrderHistoryItem } from '~/lib/data-contract/auto-generated/customer-model';
import { Modal, Typography } from '~/shared/components';
import LoadMore from '~/shared/components/load-more/load-more.component';
import OrderDetails from './order-details/order-details.component';
import { useTranslation } from '~/shared/utils';
import styles from './order-history.module.css';
import OrdersList from './orders-list/orders-list.component';
import { Skeleton } from '~/shared/components/skeleton/skeleton.component';

const ITEMS_PER_PAGE = 10;

const OrderHistory: FC = memo(() => {
    const [selectedOrder, setSelectedOrder] = useState<OrderHistoryItem | null>(null);
    const { translate } = useTranslation();

    const { data, loadMore, canLoadMore, isLoadingMore, isLoading } = useOrderHistory(
        ITEMS_PER_PAGE
    );

    const ordersList = data?.pages.flatMap((page) => page.orders || []) ?? [];

        const { 
            // this is the total count of all orders
            totalCount = 0, 
            // this is only the total number of successful orders, that excludes refunds etc.
            totalOrders = 0 
        } = data?.pages[0] || {};

    return (
        <>
            <div>
                <div className={styles.header}>
                    <Typography variant="display2" as="h2">
                        {translate('club.orderHistory.headline')}
                    </Typography>
                    <Typography variant="body" as="p">
                        {translate('club.orderHistory.description')}
                    </Typography>
                </div>
                <div className={styles.ordersListWrapper}>
                    {isLoading ? (
                        <Skeleton width="150px" height="22px" />
                    ) : (
                        <Typography variant="bodySm" as="div">
                            {translate('club.orderHistory.ordersCount', { totalCount : totalOrders })}
                        </Typography>
                    )}
                    <OrdersList
                        orders={ordersList}
                        isLoading={isLoading}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onSelect={setSelectedOrder}
                    />
                </div>
                {totalCount > 0 && ordersList.length > 0 && (
                    <LoadMore
                        canLoadMore={canLoadMore}
                        onLoadMore={loadMore}
                        isLoadingMore={isLoadingMore}
                        totalCount={totalCount}
                        currentCount={ordersList.length}
                        itemsText={translate('club.orderHistory.orders')}
                        buttonText={translate('club.orderHistory.loadMoreButton')}
                    />
                )}
            </div>
            <Modal
                scrollLock
                isOpen={!!selectedOrder}
                onClose={() => setSelectedOrder(null)}
                variant="drawerRight"
                darkHeader
                headline={translate('club.orderHistory.orderDetailsTitle')}
            >
                <OrderDetails order={selectedOrder} />
            </Modal>
        </>
    );
});

OrderHistory.displayName = 'OrderHistory';
export default OrderHistory;
